import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, AlertCircle, Check, X } from 'lucide-react';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import type { User, SubmissionAsset } from '@crewcast/shared';
import AssetLightbox from './AssetLightbox';
import '../styles/submissions.css';

interface PendingAsset {
  id: string;
  file: File;
  previewUrl: string;
  posterUrl?: string;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  s3Url?: string;
  s3Key?: string;
  errorMessage?: string;
}

interface SubmissionFormProps {
  challengeId: string;
  collaboratorsEnabled: boolean;
  onSuccess?: () => void;
}

const VALID_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_ASSETS = 10;

function generateVideoPoster(blobUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = blobUrl;
    const timeout = setTimeout(() => resolve(''), 5000);
    const draw = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 320;
        const ctx = canvas.getContext('2d');
        if (ctx && video.videoWidth > 0) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        } else {
          resolve('');
        }
      } catch {
        resolve('');
      }
    };
    video.addEventListener('seeked', draw, { once: true });
    video.addEventListener('loadeddata', () => { video.currentTime = 0.1; }, { once: true });
    video.addEventListener('error', () => { clearTimeout(timeout); resolve(''); }, { once: true });
    video.load();
  });
}

export default function SubmissionForm({
  challengeId,
  collaboratorsEnabled,
  onSuccess,
}: SubmissionFormProps) {
  const { token } = useAuth();
  const pointsEnabled = usePointsEnabled();
  const [pendingAssets, setPendingAssets] = useState<PendingAsset[]>([]);
  const [caption, setCaption] = useState('');
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCollaboratorSearch, setShowCollaboratorSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      pendingAssets.forEach((a) => {
        if (a.previewUrl.startsWith('blob:')) URL.revokeObjectURL(a.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    if (collaboratorsEnabled) fetchEmployees();
  }, [collaboratorsEnabled]);

  const fetchEmployees = async () => {
    try {
      if (!token) return;
      const response = await fetch(`${authConfig.apiUrl}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const uploadAsset = async (asset: PendingAsset): Promise<void> => {
    if (!token) throw new Error('Not authenticated');

    try {
      const presignedResponse = await fetch(`${authConfig.apiUrl}/api/upload/presigned-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fileName: asset.file.name,
          fileType: asset.file.type,
          purpose: 'submission',
        }),
      });

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json();
        throw new Error(errorData.message || 'Failed to get upload URL');
      }

      const { uploadUrl, publicUrl, key } = await presignedResponse.json();

      setPendingAssets((prev) =>
        prev.map((a) => (a.id === asset.id ? { ...a, progress: 20 } : a))
      );

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': asset.file.type },
        body: asset.file,
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload file to S3');

      setPendingAssets((prev) =>
        prev.map((a) =>
          a.id === asset.id
            ? { ...a, status: 'ready', progress: 100, s3Url: publicUrl, s3Key: key }
            : a
        )
      );
    } catch (err: any) {
      setPendingAssets((prev) =>
        prev.map((a) =>
          a.id === asset.id
            ? { ...a, status: 'error', progress: 0, errorMessage: err.message || 'Upload failed' }
            : a
        )
      );
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setError('');
    const remaining = MAX_ASSETS - pendingAssets.length;

    if (files.length > remaining) {
      setError(
        `You can only add ${remaining} more asset${remaining !== 1 ? 's' : ''} (max ${MAX_ASSETS} total).`
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    for (const file of files) {
      if (!VALID_TYPES.includes(file.type)) {
        setError('Please upload images (JPG, PNG, GIF, WEBP) or videos (MP4, MOV) only.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`Each file must be under 100MB. "${file.name}" exceeds this limit.`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
    }

    const newAssets: PendingAsset[] = files.map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      file,
      previewUrl: URL.createObjectURL(file),
      status: 'uploading',
      progress: 0,
    }));

    setPendingAssets((prev) => [...prev, ...newAssets]);

    newAssets.forEach(async (asset) => {
      uploadAsset(asset);
      if (asset.file.type.startsWith('video/')) {
        const posterUrl = await generateVideoPoster(asset.previewUrl);
        if (posterUrl) {
          setPendingAssets((prev) =>
            prev.map((a) => (a.id === asset.id ? { ...a, posterUrl } : a))
          );
        }
      }
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAsset = (id: string) => {
    setPendingAssets((prev) => {
      const asset = prev.find((a) => a.id === id);
      if (asset?.previewUrl.startsWith('blob:')) URL.revokeObjectURL(asset.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
    setPreviewIndex(null);
  };

  const retryUpload = (id: string) => {
    const asset = pendingAssets.find((a) => a.id === id);
    if (!asset) return;
    const retryAsset = { ...asset, status: 'uploading' as const, progress: 0, errorMessage: undefined };
    setPendingAssets((prev) =>
      prev.map((a) => (a.id === id ? retryAsset : a))
    );
    uploadAsset(retryAsset);
  };

  const toggleCollaborator = (userId: string) => {
    setSelectedCollaborators((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const allReady = pendingAssets.length > 0 && pendingAssets.every((a) => a.status === 'ready');
    if (!allReady) return;

    setError('');
    setSuccess(false);
    setSubmitting(true);

    try {
      if (!token) throw new Error('Not authenticated');

      const submissionAssets: SubmissionAsset[] = pendingAssets.map((a, i) => ({
        url: a.s3Url!,
        key: a.s3Key!,
        type: a.file.type.startsWith('video/') ? 'video' : 'image',
        fileName: a.file.name,
        order: i,
      }));

      const submissionResponse = await fetch(`${authConfig.apiUrl}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          challengeId,
          submissionAssets,
          caption: caption.trim() || undefined,
          collaborators: selectedCollaborators.length > 0 ? selectedCollaborators : undefined,
        }),
      });

      if (!submissionResponse.ok) {
        const errorData = await submissionResponse.json();
        throw new Error(errorData.message || 'Failed to create submission');
      }

      setSuccess(true);
      setSubmitting(false);
      pendingAssets.forEach((a) => {
        if (a.previewUrl.startsWith('blob:')) URL.revokeObjectURL(a.previewUrl);
      });
      setPendingAssets([]);
      setCaption('');
      setSelectedCollaborators([]);

      if (onSuccess) setTimeout(onSuccess, 1000);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit. Please try again.');
      setSubmitting(false);
    }
  };

  const filteredEmployees = employees.filter((emp) =>
    `${emp.firstName} ${emp.lastName} ${emp.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const readyCount = pendingAssets.filter((a) => a.status === 'ready').length;
  const hasErrors = pendingAssets.some((a) => a.status === 'error');
  const allReady = pendingAssets.length > 0 && pendingAssets.every((a) => a.status === 'ready');
  const canAddMore = pendingAssets.length < MAX_ASSETS;

  return (
    <div className="submission-form-container">
      {success && (
        <div className="success-message" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <CheckCircle2 size={16} /> Submission successful! Your entry is pending review.
        </div>
      )}

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="submission-form">
        {/* File Upload */}
        <div className="form-group">
          <label>Upload Content *</label>
          <p className="field-hint">
            Upload up to {MAX_ASSETS} images or videos (max 100MB each). Supported: JPG, PNG, GIF, WEBP, MP4, MOV
          </p>

          {/* Asset Gallery */}
          {pendingAssets.length > 0 && (
            <div className="asset-gallery">
              {pendingAssets.map((asset, idx) => (
                <div key={asset.id} className={`asset-thumbnail asset-thumbnail--${asset.status}`}>
                  {asset.file.type.startsWith('video/') ? (
                    <video
                      src={asset.previewUrl}
                      poster={asset.posterUrl}
                      className="thumbnail-media"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img src={asset.previewUrl} alt={asset.file.name} className="thumbnail-media" />
                  )}

                  {/* Tap target to open preview lightbox */}
                  <button
                    type="button"
                    className="thumbnail-tap-target"
                    onClick={() => setPreviewIndex(idx)}
                    aria-label={`Preview ${asset.file.name}`}
                  />

                  {asset.status === 'uploading' && (
                    <div className="thumbnail-overlay">
                      <div className="upload-spinner" />
                      <span className="thumbnail-overlay-text">{asset.progress}%</span>
                    </div>
                  )}
                  {asset.status === 'error' && (
                    <div className="thumbnail-overlay thumbnail-overlay--error">
                      <span className="thumbnail-overlay-text">{asset.errorMessage || 'Failed'}</span>
                      <button
                        type="button"
                        className="thumbnail-retry-btn"
                        onClick={() => retryUpload(asset.id)}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {asset.status === 'ready' && (
                    <div className="thumbnail-ready-check"><Check size={14} /></div>
                  )}

                  <button
                    type="button"
                    className="thumbnail-remove-btn"
                    onClick={() => removeAsset(asset.id)}
                    aria-label={`Remove ${asset.file.name}`}
                    disabled={submitting}
                  >
                    <X size={14} />
                  </button>

                  <div className="thumbnail-filename">{asset.file.name}</div>
                </div>
              ))}
            </div>
          )}

          {/* Upload status summary */}
          {pendingAssets.length > 0 && !allReady && !hasErrors && (
            <p className="upload-status-summary">
              Uploading... {readyCount} of {pendingAssets.length} ready
            </p>
          )}
          {hasErrors && (
            <p className="upload-status-summary upload-status-summary--error">
              Some files failed to upload. Remove them or retry before submitting.
            </p>
          )}
          {allReady && (
            <p className="upload-status-summary upload-status-summary--ready">
              All {pendingAssets.length} file{pendingAssets.length !== 1 ? 's' : ''} ready to submit.
            </p>
          )}

          {canAddMore ? (
            <label className="file-input-label">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,video/quicktime,video/x-m4v"
                multiple
                onChange={handleFileSelect}
                disabled={submitting}
                className="file-input-hidden"
              />
              {pendingAssets.length === 0
                ? 'Choose files'
                : `+ Add more (${pendingAssets.length}/${MAX_ASSETS})`}
            </label>
          ) : (
            <p className="field-hint">Maximum of {MAX_ASSETS} assets reached.</p>
          )}
        </div>

        {/* Caption */}
        <div className="form-group">
          <label htmlFor="caption">Caption (Optional)</label>
          <textarea
            id="caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Add a description or context for your submission..."
            rows={4}
            disabled={submitting}
            maxLength={500}
          />
          <span className="char-count">{caption.length}/500</span>
        </div>

        {/* Collaborators (if enabled) */}
        {collaboratorsEnabled && (
          <div className="form-group">
            <label>Team Members (Optional)</label>
            <p className="field-hint">
              Select teammates who collaborated on this submission.
              {pointsEnabled && " They'll also receive points when approved."}
            </p>

            <button
              type="button"
              onClick={() => setShowCollaboratorSearch(!showCollaboratorSearch)}
              className="collaborator-toggle-btn"
              disabled={submitting}
            >
              {showCollaboratorSearch ? '− Hide' : '+'} Add Collaborators
              {selectedCollaborators.length > 0 && (
                <span className="selected-count">({selectedCollaborators.length} selected)</span>
              )}
            </button>

            {showCollaboratorSearch && (
              <div className="collaborator-search-container">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="collaborator-search-input"
                  disabled={submitting}
                />
                <div className="collaborators-list">
                  {filteredEmployees.length === 0 ? (
                    <p className="no-results">No employees found</p>
                  ) : (
                    filteredEmployees.map((emp) => (
                      <label key={emp.userId} className="collaborator-item">
                        <input
                          type="checkbox"
                          checked={selectedCollaborators.includes(emp.userId)}
                          onChange={() => toggleCollaborator(emp.userId)}
                          disabled={submitting}
                        />
                        <div className="collaborator-info">
                          <span className="collaborator-name">
                            {emp.firstName} {emp.lastName}
                          </span>
                          <span className="collaborator-email">{emp.email}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {selectedCollaborators.length > 0 && (
              <div className="selected-collaborators">
                <strong>Selected:</strong>
                {selectedCollaborators.map((userId) => {
                  const emp = employees.find((e) => e.userId === userId);
                  return (
                    <span key={userId} className="collaborator-tag">
                      {emp?.firstName} {emp?.lastName}
                      <button
                        type="button"
                        onClick={() => toggleCollaborator(userId)}
                        className="remove-collaborator-btn"
                        disabled={submitting}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={!allReady || submitting || hasErrors}
            className="submit-btn"
          >
            {submitting
              ? 'Submitting...'
              : pendingAssets.length > 1
              ? `Submit ${pendingAssets.length} Assets`
              : 'Submit Entry'}
          </button>
        </div>
      </form>

      {previewIndex !== null && (
        <AssetLightbox
          assets={pendingAssets.map((a) => ({
            url: a.previewUrl,
            type: a.file.type.startsWith('video/') ? 'video' : 'image',
          }))}
          index={previewIndex}
          onClose={() => setPreviewIndex(null)}
          onIndexChange={setPreviewIndex}
        />
      )}
    </div>
  );
}
